import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/context/AuthContext";
import DownloadLinks from "@/components/DownloadLinks";

export default function Register() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!accepted) {
      setErr(lang === "en"
        ? "Please accept the Terms and Privacy Policy"
        : "Por favor acepta los Términos y la Política de Privacidad");
      return;
    }
    if (form.password.length < 6) {
      setErr(lang === "en" ? "Password must be at least 6 characters" : "La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, lang });
      localStorage.setItem("sns_token", data.token);
      localStorage.setItem("sns_user", JSON.stringify(data.user));
      if (setUser) setUser(data.user);
      // Trigger push registration silently
      import("@/lib/push").then((m) => m.registerPushIfPossible().catch(() => {}));
      // If ?plan=pro was in URL, send straight to /plan so user can pay now
      const planParam = new URLSearchParams(window.location.search).get("plan");
      nav(planParam === "pro" ? "/plan" : "/");
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl sns-fade-up">
        <button onClick={() => nav("/login")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6" data-testid="btn-back-login">
          <ArrowLeft size={16} /> {lang === "en" ? "Back to login" : "Volver al login"}
        </button>

        <div className="flex justify-center mb-6">
          <img src="/images/logo-brand.png" alt="Smile & Shine" className="h-24 rounded-3xl shadow-lg shadow-slate-300/50" />
        </div>

        <h1 className="sns-title text-3xl mb-2 text-center">
          {lang === "en" ? "Start your free trial" : "Empieza tu prueba gratis"}
        </h1>
        <p className="text-slate-500 mb-6 text-center">
          {lang === "en"
            ? "14 days of full Pro access. No credit card required."
            : "14 días de acceso Pro completo. Sin tarjeta de crédito."}
        </p>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <div className="text-xs font-bold tracking-wider text-slate-500 mb-3 text-center">
            {lang === "en" ? "AVAILABLE EVERYWHERE" : "DISPONIBLE DONDE QUIERAS"}
          </div>
          <DownloadLinks lang={lang} compact />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="block text-sm font-semibold mb-2">
              {lang === "en" ? "Company name" : "Nombre de la empresa"}
            </label>
            <input
              type="text"
              placeholder={lang === "en" ? "e.g. Coastal Cleaning Ltd." : "Ej. Limpiezas Pollença SL"}
              className="sns-input"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              required
              data-testid="input-company"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">
              {lang === "en" ? "Your name" : "Tu nombre"}
            </label>
            <input
              type="text"
              className="sns-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="input-name"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Email</label>
            <input
              type="email"
              className="sns-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value.trim().toLowerCase() })}
              required
              data-testid="input-email"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">
              {lang === "en" ? "Password (min 6 chars)" : "Contraseña (mín. 6 caracteres)"}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="sns-input pr-12"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                data-testid="input-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-600 pt-2">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1"
              data-testid="chk-terms"
            />
            <span>
              {lang === "en" ? "I accept the " : "Acepto los "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[var(--coral)] underline">
                {lang === "en" ? "Terms" : "Términos"}
              </a>
              {lang === "en" ? " and " : " y la "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[var(--coral)] underline">
                {lang === "en" ? "Privacy Policy" : "Política de Privacidad"}
              </a>
            </span>
          </label>

          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="register-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="sns-btn sns-btn-accent w-full text-base py-4 disabled:opacity-60"
            data-testid="btn-create-account"
          >
            {loading
              ? lang === "en" ? "Creating..." : "Creando..."
              : lang === "en" ? "Create account & start trial" : "Crear cuenta y empezar prueba"}
          </button>

          <div className="text-center pt-4 border-t mt-2">
            <p className="text-sm text-slate-500">
              {lang === "en" ? "Are you an employee?" : "¿Eres empleado?"}{" "}
              <button type="button" onClick={() => nav("/unirse")} className="text-sky-700 font-semibold hover:underline" data-testid="link-to-join">
                {lang === "en" ? "Join with code" : "Únete con un código"}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
